import type {
  Activity as ActivitySnake,
  ActivityDetail as ActivityDetailSnake,
  UpdateActivityBody,
} from '@kizen/api/activities';

import styled from '@emotion/styled';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import Select from '__components/Inputs/Select';
import {
  Entities,
  useSelectTypeaheadWithScroll,
} from '__components/Inputs/Select/hooks';
import Loader from '__components/Kizen/Loader';
import Switch from '__components/Kizen/Switch';
import BasicModal from '__components/Modals/presets/BasicModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import {
  activityQuery,
  searchScheduledActivitiesQuery,
} from 'query/activities';
import { snakeToCamelCaseKeys } from '__services/helpers';

type Action =
  Required<UpdateActivityBody>['disable_options']['scheduled_activities_action'];

type Activity = SnakeToCamelCase<ActivitySnake>;

type ActivityDetail = SnakeToCamelCase<ActivityDetailSnake>;

type DisableActivityModalProps = {
  activity: Activity | ActivityDetail;
  onConfirm: (data: {
    action: Action | null;
    destinationActivityId: string | null;
    sendCalendarSyncEmails: boolean;
  }) => Promise<void>;
  onHide: () => void;
};

type DeleteActivityModalProps = {
  activity: SnakeToCamelCase<Activity>;
  onConfirm: (data: { sendCalendarSyncEmails: boolean }) => Promise<void>;
  onHide: () => void;
};

type Option<T> = {
  value: T;
  label: string;
};

const Modal = styled(BasicModal)`
  .modal-body {
    overflow: unset;
    width: 600px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 15px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 10px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;

  &::before {
    content: '•';
    font-size: 16px;
  }
`;

const WarningBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px;
  background-color: var(--color-feedback-info-bg);
`;

const StyledSelect = styled(Select)`
  > div:last-child {
    width: 265px;
  }
`;

const getAssociationDifference = (
  src: ActivityDetail,
  dest: ActivityDetail
): { isCompatible: boolean; objectNames: string[] } => {
  if (dest.associationMode === 'all_objects_associated') {
    return { isCompatible: true, objectNames: [] };
  }

  if (dest.associationMode === 'no_objects_associated') {
    return {
      isCompatible: src.associationMode === 'no_objects_associated',
      objectNames: [],
    };
  }

  if (src.associationMode === 'no_objects_associated') {
    return { isCompatible: false, objectNames: [] };
  }

  if (src.associationMode === 'all_objects_associated') {
    return { isCompatible: true, objectNames: [] };
  }

  const srcObjects = src.associatedObjects;
  const destObjects = dest.associatedObjects;
  const srcIds = new Set(srcObjects.map((obj) => obj.customObject.id));
  const destIds = new Set(destObjects.map((obj) => obj.customObject.id));
  const missingIds = srcIds.difference(destIds);

  if (!missingIds.size) {
    return { isCompatible: true, objectNames: [] };
  }

  const objectNames = srcObjects.reduce<string[]>((acc, obj) => {
    if (missingIds.has(obj.customObject.id)) {
      acc.push(obj.customObject.objectName);
    }
    return acc;
  }, []);

  return { isCompatible: false, objectNames };
};

const isActivityDetail = (
  activity: Activity | ActivityDetail
): activity is ActivityDetail => {
  return 'associatedObjects' in activity;
};

export const DeleteActivityModal = (props: DeleteActivityModalProps) => {
  const { activity, onConfirm, onHide } = props;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const [sendCalendarSyncEmails, setSendCalendarSyncEmails] = useState(true);

  const query = useQuery(
    searchScheduledActivitiesQuery({
      params: { activity_id: activity.id, completed: false },
      staleTime: 0,
      gcTime: 0,
    })
  );

  if (query.isLoading) {
    return (
      <Modal
        show
        disabled
        size="md"
        heading={t('Delete Scheduled Activities')}
        buttonText={t('Confirm Delete')}
        onHide={onHide}
      >
        <Loader loading />
      </Modal>
    );
  }

  const scheduledActivitiesCount = query.data?.count ?? 0;
  const activityPluralized =
    scheduledActivitiesCount === 1 ? t('activity') : t('activities');

  return (
    <Modal
      show
      heading={t('Delete Scheduled Activities')}
      size="md"
      buttonText={t('Confirm Delete')}
      actionBtnColor="red"
      onHide={onHide}
      onConfirm={async () => {
        try {
          await onConfirm({ sendCalendarSyncEmails });

          showToast({
            message: t(
              'Deletion queued for {{count}} scheduled ‘{{name}}’ {{plural}}',
              {
                count: scheduledActivitiesCount,
                name: activity.name,
                plural: activityPluralized,
              }
            ),
          });
        } catch (err) {
          showToast({
            variant: toastVariant.FAILURE,
            message: t(
              'There was an issue starting the deletion of these scheduled activities. Please try again.'
            ),
          });

          throw err;
        }
      }}
    >
      <Container>
        <KizenTypography>
          {t('This will delete {{count}} currently scheduled {{plural}}.', {
            count: scheduledActivitiesCount,
            plural: activityPluralized,
          })}
        </KizenTypography>
        {Boolean(activity.calendarSyncEnabled) && (
          <Switch
            removeMargin
            checked={sendCalendarSyncEmails}
            label={t('Email calendar cancellations to attendees')}
            onChange={(ev) => {
              setSendCalendarSyncEmails(ev.target.checked);
            }}
          />
        )}
      </Container>
    </Modal>
  );
};

export const DisableActivityModal = (props: DisableActivityModalProps) => {
  const { activity, onConfirm, onHide } = props;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const [action, setAction] = useState<Action | null>(null);
  const [sendCalendarSyncEmails, setSendCalendarSyncEmails] = useState(true);
  const [destination, setDestination] = useState<Option<string> | null>(null);
  const destinationId = destination?.value ?? null;
  const destinationRef = useRef<HTMLDivElement | null>(null);

  const query = useQuery(
    searchScheduledActivitiesQuery({
      params: { activity_id: activity.id, completed: false },
      staleTime: 0,
      gcTime: 0,
    })
  );

  const [destinationActivitiesProps] = useSelectTypeaheadWithScroll({
    selectRef: destinationRef,
    entity: Entities.Activities,
    enabled: action === 'migrate',
    params: { enabled: true },
    chosenValueIds: [activity.id],
  });

  const sourceQuery = useQuery(
    activityQuery(activity.id, {
      enabled: !isActivityDetail(activity),
      select: (data) => {
        return snakeToCamelCaseKeys(data);
      },
    })
  );

  const source = isActivityDetail(activity) ? activity : sourceQuery.data;

  const destinationQuery = useQuery(
    activityQuery(destinationId ?? '', {
      enabled: Boolean(destinationId),
      select: (data) => {
        return snakeToCamelCaseKeys(data);
      },
    })
  );

  if (query.isLoading) {
    return (
      <Modal
        show
        disabled
        size="md"
        heading={t('Disable Activity')}
        buttonText={t('Disable')}
        onHide={onHide}
      >
        <Loader loading />
      </Modal>
    );
  }

  const scheduledActivitiesCount = query.data?.count ?? 0;
  const hasScheduledActivities = scheduledActivitiesCount > 0;
  const confirmDisabled =
    hasScheduledActivities && (action === 'migrate' ? !destinationId : !action);
  const buttonColor = action === 'keep' ? 'blue' : 'red';
  const showCalendarToggle =
    activity.calendarSyncEnabled && action === 'delete';
  const activityPluralized =
    scheduledActivitiesCount === 1 ? t('activity') : t('activities');

  let buttonText = t('Disable');
  if (action === 'delete') {
    buttonText = t('Disable & Delete Scheduled');
  } else if (action === 'keep') {
    buttonText = t('Disable & Keep Scheduled');
  } else if (action === 'migrate') {
    buttonText = t('Disable & Migrate Scheduled');
  }

  const associationData =
    !!destinationQuery.data && source
      ? getAssociationDifference(source, destinationQuery.data)
      : { isCompatible: true, objectNames: [] };

  return (
    <Modal
      show
      heading={t('Disable Activity')}
      size="md"
      buttonText={buttonText}
      actionBtnColor={buttonColor}
      disabled={confirmDisabled}
      onHide={onHide}
      onConfirm={async () => {
        try {
          await onConfirm({
            action,
            destinationActivityId: destinationId,
            sendCalendarSyncEmails:
              action === 'delete' && sendCalendarSyncEmails,
          });

          if (action === 'delete' && scheduledActivitiesCount > 0) {
            showToast({
              message: t(
                'Deletion queued for {{count}} scheduled ‘{{name}}’ {{plural}}',
                {
                  count: scheduledActivitiesCount,
                  name: activity.name,
                  plural: activityPluralized,
                }
              ),
            });
          } else if (action === 'migrate') {
            showToast({
              message: t(
                'Migration queued for {{count}} scheduled activities from ‘{{source}}’ to ‘{{destination}}’',
                {
                  count: scheduledActivitiesCount,
                  source: activity.name,
                  destination: destinationQuery.data?.name,
                }
              ),
            });
          } else {
            showToast({
              message: t("Activity '{{name}}' has been disabled.", {
                name: activity.name,
              }),
            });
          }
        } catch (err) {
          showToast({
            variant: toastVariant.FAILURE,
            message: t(
              'There was an issue updating the activity. Please try again.'
            ),
          });

          throw err;
        }
      }}
    >
      <Container>
        <Typography>
          {t(
            'This will prevent new {{name}} activities from being scheduled or logged.',
            { name: activity.name }
          )}
        </Typography>
        {hasScheduledActivities && (
          <>
            <StyledSelect
              label={`${t(
                'Action to take on {{count}} currently scheduled {{plural}}',
                {
                  count: scheduledActivitiesCount,
                  plural: activityPluralized,
                }
              )}:`}
              placeholder={t('Choose Action')}
              value={action}
              onChange={(option: Option<Action>) => {
                setAction(option.value);
                setDestination(null);
              }}
              options={
                [
                  { value: 'delete', label: t('Delete') },
                  { value: 'keep', label: t('Keep') },
                  {
                    value: 'migrate',
                    label: t('Migrate to another Activity'),
                  },
                ] satisfies Option<Action>[]
              }
            />
            {showCalendarToggle && (
              <Switch
                removeMargin
                checked={sendCalendarSyncEmails}
                label={t('Email calendar cancellations to attendees')}
                onChange={(ev) => {
                  setSendCalendarSyncEmails(ev.target.checked);
                }}
              />
            )}
            {action === 'migrate' && (
              <>
                <StyledSelect
                  ref={destinationRef}
                  label={t('Destination Activity')}
                  placeholder={t('Choose Activity')}
                  selectWidth="300px"
                  value={destination}
                  onChange={(option: Option<string>) => {
                    setDestination(option);
                  }}
                  {...destinationActivitiesProps}
                />
                <>
                  {!associationData.isCompatible && (
                    <WarningBlock>
                      <Typography color="button-warning-hover">
                        {t('Warning: Potential Data Loss')}
                      </Typography>
                      <Typography weight="bold">
                        {t(
                          'The Source and Destination Activities do not share the same object associations.'
                        )}
                      </Typography>
                      {associationData.objectNames.length > 0 && (
                        <>
                          <Typography>
                            {t(
                              'Associations unique to the Source Activity will not be migrated:'
                            )}
                          </Typography>
                          <List>
                            {associationData.objectNames.map((name) => (
                              <ListItem key={name}>
                                <Typography>{name}</Typography>
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    </WarningBlock>
                  )}
                  {!activity.isAssociationRequired &&
                    destinationQuery.data?.isAssociationRequired && (
                      <WarningBlock>
                        <Typography weight="bold">
                          {t(
                            'Destination Activity requires at least one associated object.'
                          )}
                        </Typography>
                        <Typography>
                          {t(
                            'Activity records that do not meet this requirement will be skipped during migration.'
                          )}
                        </Typography>
                      </WarningBlock>
                    )}
                </>
              </>
            )}
          </>
        )}
      </Container>
    </Modal>
  );
};
